import React, { useState, useEffect } from "react";
import PageInstructions from "@/components/PageInstructions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface Grupo {
  nome: string;
  emoji: string;
  centenas: string[];
}

const CentenaBichos = () => {
  const [grupoSelecionado, setGrupoSelecionado] = useState<string>("");
  const [diasAtraso, setDiasAtraso] = useState(6);
  const [showResults, setShowResults] = useState(false);

  const grupos: { [key: string]: Grupo } = {
    "15": {
      nome: 'Jacaré',
      emoji: '🐊',
      centenas: [
        '057', '058', '059', '060',
        '157', '158', '159', '160',
        '257', '258', '259', '260',
        '357', '358', '359', '360',
        '457', '458', '459', '460',
        '557', '558', '559', '560',
        '657', '658', '659', '660',
        '757', '758', '759', '760',
        '857', '858', '859', '860',
        '957', '958', '959', '960'
      ]
    },
    "8": {
      nome: 'Camelo',
      emoji: '🐪',
      centenas: [
        '029', '030', '031', '032',
        '129', '130', '131', '132',
        '229', '230', '231', '232',
        '329', '330', '331', '332',
        '429', '430', '431', '432',
        '529', '530', '531', '532',
        '629', '630', '631', '632',
        '729', '730', '731', '732',
        '829', '830', '831', '832',
        '929', '930', '931', '932'
      ]
    },
    "24": {
      nome: 'Veado',
      emoji: '🦌',
      centenas: [
        '093', '094', '095', '096',
        '193', '194', '195', '196',
        '293', '294', '295', '296',
        '393', '394', '395', '396',
        '493', '494', '495', '496',
        '593', '594', '595', '596',
        '693', '694', '695', '696',
        '793', '794', '795', '796',
        '893', '894', '895', '896',
        '993', '994', '995', '996'
      ]
    }
  };

  const getAlertMessage = () => {
    if (!grupoSelecionado) {
      return { type: 'warning', message: '⚠️ Selecione um grupo para continuar' };
    }
    
    if (diasAtraso < 6) {
      return { type: 'warning', message: '⏰ Recomendação: Aguarde pelo menos 6 dias de atraso antes de apostar' };
    } else if (diasAtraso >= 6 && diasAtraso <= 8) {
      return { type: 'success', message: '✅ Momento ideal para apostar! O grupo está no período recomendado' };
    } else if (diasAtraso > 8) {
      return { type: 'success', message: '🚨 Oportunidade máxima! O grupo passou do limite histórico de 8 dias' };
    }
    
    return null;
  };

  const gerarCentenas = () => {
    if (!grupoSelecionado) {
      alert('Por favor, selecione um grupo!');
      return;
    }
    
    setShowResults(true);
  };

  const handleGroupSelect = (grupo: string) => {
    setGrupoSelecionado(grupo);
    setShowResults(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      gerarCentenas();
    }
  };

  const alertInfo = getAlertMessage();
  const grupo = grupoSelecionado ? grupos[grupoSelecionado] : null;

  return (
    <div className="w-full space-y-6">
      <PageInstructions
        title="📖 Como usar a Técnica da Centena"
        steps={[
          "Veja a lista dos 25 grupos com seus dias de atraso",
          "Selecione o grupo que está com mais dias sem sair",
          "O sistema recomenda apostar quando o atraso for de pelo menos 6 dias",
          "Veja as centenas mais frequentes do grupo selecionado",
          "Aposte nas centenas sugeridas para maximizar suas chances"
        ]}
      />
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-blue-700 bg-clip-text text-transparent">
          🎯 Técnica da Centena
        </h1>
        <p className="text-xl text-muted-foreground">Estratégia para os Grupos com Melhor Índice de Saída</p>
      </div>

      {/* Description */}
      <Card className="border-l-4 border-l-indigo-600 bg-gradient-to-r from-slate-50 to-gray-50">
        <CardContent className="p-6">
          <p className="text-gray-700 leading-relaxed">
            <strong className="text-gray-800">Estratégia comprovada:</strong> Os grupos 15 (Jacaré), 8 (Camelo) e 24 (Veado) têm os melhores índices de saída, sendo sorteados em média a cada 7 resultados. O máximo de dias sem aparecer são 8 dias. Jogue após 6 dias de atraso no grupo mais atrasado, apostando em todas as 40 centenas do 1º ao 5º prêmio.
          </p>
        </CardContent>
      </Card>

      {/* Featured Groups */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white border-0 hover:scale-105 transition-transform duration-300">
          <CardContent className="p-6 text-center">
            <div className="text-4xl font-bold mb-2">15</div>
            <div className="text-2xl mb-4 uppercase tracking-wide">🐊 Jacaré</div>
            <div className="bg-white/20 p-3 rounded-lg backdrop-blur-sm">
              <div className="text-sm">Centenas: 057-060</div>
              <div className="text-sm">Frequência: A cada 7 sorteios</div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white border-0 hover:scale-105 transition-transform duration-300">
          <CardContent className="p-6 text-center">
            <div className="text-4xl font-bold mb-2">08</div>
            <div className="text-2xl mb-4 uppercase tracking-wide">🐪 Camelo</div>
            <div className="bg-white/20 p-3 rounded-lg backdrop-blur-sm">
              <div className="text-sm">Centenas: 029-032</div>
              <div className="text-sm">Frequência: A cada 7 sorteios</div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white border-0 hover:scale-105 transition-transform duration-300">
          <CardContent className="p-6 text-center">
            <div className="text-4xl font-bold mb-2">24</div>
            <div className="text-2xl mb-4 uppercase tracking-wide">🦌 Veado</div>
            <div className="bg-white/20 p-3 rounded-lg backdrop-blur-sm">
              <div className="text-sm">Centenas: 093-096</div>
              <div className="text-sm">Frequência: A cada 7 sorteios</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Control Section */}
      <Card className="border-2 border-slate-200 bg-gradient-to-br from-slate-50 to-gray-50">
        <CardHeader>
          <CardTitle className="text-slate-700 text-xl text-center">🎲 Selecione o Grupo e Configure a Estratégia</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Group Selector */}
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="text-lg font-bold text-gray-800 mb-4">📋 Primeiro, selecione um grupo:</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(grupos).map(([grupoNum, grupoInfo]) => (
                <div
                  key={grupoNum}
                  onClick={() => handleGroupSelect(grupoNum)}
                  className={`p-4 border-2 rounded-xl text-center cursor-pointer transition-all duration-300 hover:scale-105 ${
                    grupoSelecionado === grupoNum
                      ? 'border-indigo-600 bg-gradient-to-br from-indigo-600 to-blue-700 text-white'
                      : 'border-gray-300 bg-white hover:border-indigo-600 hover:shadow-lg text-gray-800'
                  }`}
                >
                  <div className="text-lg font-bold">Grupo {grupoNum}</div>
                  <div className="text-sm opacity-80">{grupoInfo.emoji} {grupoInfo.nome}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Days Input */}
          <div className="flex justify-center items-center gap-4 flex-wrap">
            <label className="font-semibold text-gray-800">Dias sem aparecer:</label>
            <input
              type="number"
              min="0"
              max="15"
              value={diasAtraso}
              onChange={(e) => setDiasAtraso(parseInt(e.target.value) || 0)}
              onKeyPress={handleKeyPress}
              className="w-24 p-3 border-2 border-gray-300 rounded-lg text-center font-bold text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
            />
            <span className="text-gray-600 text-sm">(Recomendado: 6+ dias)</span>
          </div>

          {/* Alert Area */}
          {alertInfo && (
            <div className={`p-4 rounded-lg text-center font-bold ${
              alertInfo.type === 'success' 
                ? 'bg-green-100 border border-green-300 text-green-800' 
                : 'bg-yellow-100 border border-yellow-300 text-yellow-800'
            }`}>
              {alertInfo.message}
            </div>
          )}

          {/* Generate Button */}
          <Button
            onClick={gerarCentenas}
            disabled={!grupoSelecionado}
            className="w-full bg-gradient-to-r from-indigo-600 to-blue-700 hover:from-indigo-700 hover:to-blue-800 text-white py-4 text-lg font-bold rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            🎰 Gerar Centenas para Apostas
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {showResults && grupo && (
        <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
          {/* Centenas Card */}
          <Card className="border-0">
            <CardContent className="p-0">
              <div className="bg-gradient-to-r from-orange-400 to-pink-500 text-white p-6 rounded-t-lg">
                <h3 className="text-2xl font-bold text-center mb-4">
                  📋 Centenas do Grupo {grupoSelecionado} - {grupo.emoji} {grupo.nome}
                </h3>
                
                <div className="bg-white/20 backdrop-blur-sm p-4 rounded-xl mb-5 text-center">
                  <div><strong>📊 Estratégia:</strong> {diasAtraso} dias sem aparecer</div>
                  <div><strong>🎯 Total de centenas:</strong> 40 centenas para apostar</div>
                  <div><strong>💰 Modalidade:</strong> Do 1º ao 5º prêmio (paga 160x o valor)</div>
                  {diasAtraso > 8 && (
                    <div className="text-yellow-300 font-bold mt-2">
                      ⚡ ALERTA MÁXIMO: Grupo passou do limite histórico!
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-8 sm:grid-cols-10 md:grid-cols-12 lg:grid-cols-16 gap-2">
                  {grupo.centenas.map((centena, index) => (
                    <div
                      key={index}
                      className="bg-white/90 text-slate-700 p-2 rounded-lg text-center font-bold text-sm shadow-lg"
                    >
                      {centena}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Strategy Info */}
          <Card className="border-0">
            <CardContent className="p-0">
              <div className="bg-gradient-to-r from-cyan-400 to-blue-500 text-white p-6 rounded-lg">
                <h3 className="text-2xl font-bold text-center mb-4">💰 Informações da Estratégia</h3>
                
                <div className="bg-white/20 backdrop-blur-sm p-5 rounded-xl space-y-4">
                  <p><strong>🎯 Como funciona:</strong> Aposte em todas as 40 centenas do grupo escolhido, jogando do 1º ao 5º prêmio.</p>
                  <p><strong>💵 Premiação:</strong> A centena paga 160 vezes o valor apostado quando jogando do 1º ao 5º prêmio.</p>
                  <p><strong>🎊 Bônus:</strong> Se o grupo repetir, você ganha na centena duas vezes!</p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
                    {['1º Prêmio', '2º Prêmio', '3º Prêmio', '4º Prêmio', '5º Prêmio'].map((premio) => (
                      <div key={premio} className="bg-white/10 p-3 rounded-lg text-center">
                        <strong>{premio}</strong><br />
                        Paga 160x
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CentenaBichos;